import {
  LinkApiModel,
  PaymentOptionOptionApiModel,
  PaymentOptionType,
  Service,
  ShipResponse,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { BulkSaveSelectedAction, ISmartSendContext } from '../context/SmartSendContextModels';
import { ApplierOption } from './ApplierOption';
import { SumNumber, onlyUnique } from './ArrayHelper';
import { RemoveOffset } from './DateHelper';
import { GetSelectedItems } from './GetSelectedItems';
import GenerateOptions from './SmartSendBulkActionOptions';
import { BulkTariffCodes } from '~/apps/smartsend/components/smartsend/actions/BulkTariffCodes';
import GetTariffSystemFromItem from './GetTariffSystemFromItem';

export function SmartSendBulkApplier(
  filterKey: string,
  state: ISmartSendContext,
  action: BulkSaveSelectedAction,
  smartSendItemsCache: SmartSendItemApiModel[]
) {
  const force = action.payload.force;

  GetSelectedItems(filterKey, state, smartSendItemsCache).forEach((i) => {
    let unsaved = [];

    if (!action.payload.actions) {
      return;
    }

    const options = GenerateOptions(i, i.State.validation);
    const actions = action.payload.actions || [];

    const canApply = (x: ApplierOption) => options.includes(x) && actions.includes(x) && !!action.payload.changes[x];
    const canApplyWithNull = (x: ApplierOption) => options.includes(x) && actions.includes(x);
    const canApplySubAction = (category: ApplierOption, a: ApplierOption) =>
      canApplyWithNull(a) && !!action.payload.changes[category];

    if (canApplySubAction('Dimensions', 'Dimensions:Change')) {
      if (action.payload.changes.Dimensions.Weight > 0) {
        i.PackageDimensions.Weight = action.payload.changes.Dimensions.Weight;
        unsaved.push('Dimensions');
      }
      if (action.payload.changes.Dimensions.Height > 0) {
        i.PackageDimensions.Height = action.payload.changes.Dimensions.Height;
        unsaved.push('Dimensions');
      }
      if (action.payload.changes.Dimensions.Length > 0) {
        i.PackageDimensions.Length = action.payload.changes.Dimensions.Length;
        unsaved.push('Dimensions');
      }
      if (action.payload.changes.Dimensions.Width > 0) {
        i.PackageDimensions.Width = action.payload.changes.Dimensions.Width;
        unsaved.push('Dimensions');
      }
    } else if (canApplySubAction('Dimensions', 'Dimensions:Add')) {
      if (!i.PackageDimensions.Weight) {
        if (action.payload.changes.Dimensions.Weight > 0) {
          i.PackageDimensions.Weight = action.payload.changes.Dimensions.Weight;
          unsaved.push('Dimensions');
        }
      }
      if (!i.PackageDimensions.Height) {
        if (action.payload.changes.Dimensions.Height > 0) {
          i.PackageDimensions.Height = action.payload.changes.Dimensions.Height;
          unsaved.push('Dimensions');
        }
      }
      if (!i.PackageDimensions.Length) {
        if (action.payload.changes.Dimensions.Length > 0) {
          i.PackageDimensions.Length = action.payload.changes.Dimensions.Length;
          unsaved.push('Dimensions');
        }
      }
      if (!i.PackageDimensions.Width) {
        if (action.payload.changes.Dimensions.Width > 0) {
          i.PackageDimensions.Width = action.payload.changes.Dimensions.Width;
          unsaved.push('Dimensions');
        }
      }
    }

    if (canApply('Sender')) {
      unsaved.push('Sender');
      i.Sender = action.payload.changes.Sender;
    }

    if (canApplySubAction('TariffCodes', 'TariffCodes:Add')) {
      unsaved.push('MarketplaceOrderLines');
      const changes = (action.payload.changes.TariffCodes as BulkTariffCodes)?.[GetTariffSystemFromItem(i)];
      i.MarketplaceOrderLines.forEach((m) => {
        if ((!m.Product.TariffCode || force) && changes[m.Product.Description]) {
          m.Product.TariffCode = changes[m.Product.Description];
        }
      });
    }

    if (canApply('ItemValue') && i.MarketplaceOrderLines) {
      unsaved.push('ItemValue');
      i.MarketplaceOrderLines.forEach((m) => {
        if (!m.Value || force) {
          m.Value = action.payload.changes.ItemValue;
        }
      });
      i.Value = SumNumber(i.MarketplaceOrderLines.map((x) => x.Value));
    }

    if (canApplySubAction('RecipientContactDetails', 'RecipientContactDetails:Add')) {
      if (!i.Recipient.Email) {
        i.Recipient.Email = action.payload.changes.RecipientContactDetails.Email;
        unsaved.push('RecipientContactDetails');
      }
      if (!i.Recipient.Phone) {
        i.Recipient.Phone = action.payload.changes.RecipientContactDetails.Phone;
        unsaved.push('RecipientContactDetails');
      }
    } else if (canApplySubAction('RecipientContactDetails', 'RecipientContactDetails:Change')) {
      if (action.payload.changes.RecipientContactDetails.Email) {
        i.Recipient.Email = action.payload.changes.RecipientContactDetails.Email;
        unsaved.push('RecipientContactDetails');
      }
      if (action.payload.changes.RecipientContactDetails.Phone) {
        i.Recipient.Phone = action.payload.changes.RecipientContactDetails.Phone;
        unsaved.push('RecipientContactDetails');
      }
    }

    if (canApplyWithNull('PreferredCollectionDate')) {
      if (!i.PreferredCollectionDate || force) {
        unsaved.push('PreferredCollectionDate');
        i.PreferredCollectionDate = RemoveOffset(action.payload.changes.PreferredCollectionDate as Date);
      }
    }

    if (canApply('Recipient')) {
      unsaved.push('Recipient');
      i.Recipient = action.payload.changes.Recipient;
    }

    if (canApplySubAction('Service', 'Service:Add')) {
      if (!i.Service?.Id) {
        unsaved.push('Service');
        i.Service = {
          Name: action.payload.changes.Service.Name,
          Id: action.payload.changes.Service.Id,
          Provider: action.payload.changes.Service.Provider,
          AccountId: action.payload.changes.Service.AccountId,
          Meta: null
        };
      }
    } else if (canApplySubAction('Service', 'Service:Change')) {
      if (i.Service?.Id != action.payload.changes.Service?.Id) {
        unsaved.push('Service');
        i.Service = {
          Name: action.payload.changes.Service.Name,
          Id: action.payload.changes.Service.Id,
          Provider: action.payload.changes.Service.Provider,
          AccountId: action.payload.changes.Service.AccountId,
          Meta: null
        };
      }
    }

    if (canApply('ServiceOption')) {
      unsaved.push('ServiceOption');
      action.payload.changes.ServiceOption.Extras.forEach((e) => {
        if (!i.Extras.find((x) => x.Id === e.Id)) {
          i.Extras.push({ Id: e.Id, Name: e.Name, Provider: e.Provider });
        }
      });
    }

    if (canApply('Tags')) {
      unsaved.push('Tags');
      i.Tags = action.payload.changes.Tags.concat(i.Tags).filter(onlyUnique);
    }

    if (canApply('Extras') || canApply('Protection')) {
      unsaved.push('Extras');
      action.payload.changes.Extras.forEach((e) => {
        // Has Quote
        if (i.State?.validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras) {
          if (
            !i.Extras.find((x) => x.Id === e.Id) &&
            i.State?.validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras.find((ae) => ae.Id == e.Id)
          ) {
            i.Extras.push({ Id: e.Id, Name: e.Name, Provider: e.Provider });
          }
        } else {
          if (!i.Extras.find((x) => x.Id === e.Id)) {
            i.Extras.push({ Id: e.Id, Name: e.Name, Provider: e.Provider });
          }
        }
      });
    }

    if (canApply('Dispatch')) {
      i.MarketplaceStatus = 'Dispatched';
      unsaved.push('MarkAsDispatched');
      unsaved.push('MarketplaceStatus');
    }

    if (canApply('MarkAsPackaged')) {
      i.Meta['PackagedDate'] = { Value: new Date().toString(), Type: 'DateTime' };
      unsaved.push('MarkAsPackaged');
    }

    if (canApply('MarkAsNotPackaged')) {
      i.Meta['PackagedDate'] = { Value: null, Type: 'DateTime' };
      unsaved.push('MarkAsNotPackaged');
    }

    // Unique
    unsaved = unsaved.filter(onlyUnique);

    if (unsaved.length === 1 && unsaved[0] == 'Return') {
      // ReturnCollection Bulk Action
      i.State.unsaved = unsaved;
      i.State.state = 'RETURN';
      return;
    } else {
      // Others Bulk Actions
      if (unsaved.length > 0) {
        i.State.unsaved = unsaved;
        i.State.state = 'SAVE';
      }
    }
  });
}

export interface ProviderGroup {
  Key: string;
  Provider: { AccountId: string; Provider: string };
  Items: SmartSendItemApiModel[];
  CurrencyCode: string;
  TotalPrice: number;
  Duties: number;
  TotalPriceExVat: number;
  TotalVat: number;
  VatRate: number;
  SelectedPaymentMethod: PaymentOptionType;
  SelectedPaymentOption?: PaymentOptionOptionApiModel;
  State: ProviderGroupState;
  Summary: ProviderGroupItemSummary[];
  ShipResponse?: ShipResponse;
  MarkedAsDispatched: boolean;
  Manifestable: boolean;
  Discount: DiscountDetails;
  Links?: LinkApiModel[];
}

export interface DiscountDetails {
  TotalDiscount?: number | undefined;
  DiscountCode?: string;
  Loading?: boolean;
  Applied: boolean;
  TotalPrice?: number;
  TotalVat?: number;
  TotalPriceExVat?: number;
  Breakdown?: Record<string, number>;
}

export interface ProviderGroupItemSummary {
  Service: Service;
  Count: number;
  TotalPriceExVat: number;
  TotalPrice: number;
  Duties: number;
  TotalVat: number;
  VatRate: number;
  CurrencyCode: string;
}

export type ProviderGroupState = 'BOOKING' | 'IDLE' | 'BOOKED' | 'ERROR' | 'STAGED';
